import { Box, Button, Grid, HStack, Text } from '@chakra-ui/react';
import { Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { i18n } from '@investigator/shared/localization';
import ShieldSharpIcon from '@gamma/ui/icons/ShieldSharpIcon';
import { useAgenticTriageSettings } from '../../hooks';
import {
  useEntitiesContext,
  useEntityAgenticTriageReportContext,
  useEntityEdrContext,
} from '../../context';
import { EDRIntegration } from '@investigator/features/integrations/pages/IntegrationsConstants';
import { ChatGPTIcon } from '../ChatGPTIcon';
import { RecommendedActionItem } from './RecommendedActionItem';
import { Action } from '../../types/agenticTriageReport';
import { transformRecommendedActions } from '../../utils/transformRecommendedActions';
import { useCallback, useMemo } from 'react';

export const RecommendedResponsesPanel = (): JSX.Element | null => {
  const { agenticTriageEnabled } = useAgenticTriageSettings();
  const { report } = useEntityAgenticTriageReportContext();
  const modalCopy = i18n.pages.entities.entityAnalysisModal;
  const edrActionCopy = i18n.pages.detections.entityEDR.edrAction;
  const isRecommendedToIsolateEntity =
    report?.recommended_actions_info?.to_isolate_entity;
  const { isolationModal, selectedEntity } = useEntitiesContext();
  const { edrData, isIsolated } = useEntityEdrContext();

  const isolationType = edrData?.crowdstrike?.entity_id
    ? EDRIntegration.CROWDSTRIKE
    : edrData?.microsoft_defender?.entity_id
      ? EDRIntegration.MICROSOFT_DEFENDER
      : null;

  const handleActionClick = useCallback(() => {
    if (!isolationType) return;
    isolationModal.onOpen(isolationType);
  }, [isolationType, isolationModal]);

  const actions = useMemo(
    () =>
      transformRecommendedActions(
        report?.recommended_actions_info?.recommended_actions,
      ),
    [report?.recommended_actions_info?.recommended_actions],
  );

  // Don't render panel if feature disabled or no actions/button to display
  const hasActionsToDisplay = actions && actions.length > 0;
  const hasIsolationButton = isRecommendedToIsolateEntity && isolationType;

  if (!agenticTriageEnabled || (!hasActionsToDisplay && !hasIsolationButton)) {
    return null;
  }

  return (
    <Panel w="100%">
      <PanelHeader pb={0}>
        <PanelHeading>
          <HStack spacing={2}>
            <ChatGPTIcon boxSize={6} />
            <Text>{modalCopy.recommendedResponses}</Text>
          </HStack>
        </PanelHeading>
      </PanelHeader>
      <Box>
        <Grid
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          gap={4}
        >
          {actions.map((action: Action, index: number) => (
            <RecommendedActionItem
              key={`action-${action.type}-${index}`}
              action={action}
            />
          ))}
        </Grid>

        {/* Action Button */}
        {isRecommendedToIsolateEntity && isolationType && (
          <Button
            variant="solid"
            colorScheme="gray"
            size="sm"
            mt={4}
            leftIcon={<ShieldSharpIcon boxSize={4} />}
            onClick={handleActionClick}
          >
            {isIsolated
              ? `${edrActionCopy.liftIsolation} ${selectedEntity?.entityName}`
              : `${edrActionCopy.isolateEntity} ${selectedEntity?.entityName}`}
          </Button>
        )}
        {/* Suppression recommendation is currently not on the endpoint */}
      </Box>
    </Panel>
  );
};
